
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useProducts } from "./useProducts";

export interface VariantFormValues {
  name: string;
  products: string[];
  factories: string[];
  image_url: string | null;
  description: string | null;
  add_custom_option: boolean;
  variants_order: number;
  variant_monogram?: any;
  variant_group?: any;
}

// Simple cache to avoid repeated API calls
let variantsCache: any[] | null = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export function useVariants() {
  const [variants, setVariants] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { products } = useProducts();

  // Check if cache is still valid
  const isCacheValid = useCallback(() => {
    return variantsCache && 
           cacheTimestamp && 
           (Date.now() - cacheTimestamp) < CACHE_DURATION;
  }, []);

  const fetchVariants = useCallback(async (forceRefresh = false) => {
    // Use cache if valid and not forcing refresh
    if (!forceRefresh && isCacheValid()) {
      setVariants(variantsCache!);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("variants")
        .select(`
          id,
          name,
          description,
          image_url,
          products,
          factories,
          add_custom_option,
          variants_order,
          variant_monogram,
          variant_group
        `)
        .order("variants_order", { ascending: true });

      if (error) throw error;

      // Map product IDs to product names for display
      const enhancedData = data?.map(variant => {
        return {
          ...variant,
          // Store original product IDs for editing
          originalProducts: variant.products,
          // Display product names in the table
          products: Array.isArray(variant.products) ? 
            variant.products.map((productId: string) => {
              const product = products.find(p => p.id === productId);
              return product ? product.name : productId;
            }) : 
            []
        };
      }) || [];

      // Update cache
      variantsCache = enhancedData;
      cacheTimestamp = Date.now();
      
      setVariants(enhancedData);
    } catch (error: any) {
      toast({
        title: "Error fetching variants",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [products, toast, isCacheValid]);

  const createVariant = async (values: VariantFormValues) => {
    try {
      const { error } = await supabase
        .from("variants")
        .insert({
          name: values.name,
          products: values.products, // Store product IDs
          factories: values.factories,
          description: values.description || null,
          image_url: values.image_url || null,
          add_custom_option: values.add_custom_option,
          variants_order: values.variants_order,
          variant_monogram: values.variant_monogram || {},
          variant_group: values.variant_group || {},
        });

      if (error) throw error;
      
      toast({
        title: "Variant Created",
        description: `${values.name} has been created.`
      });
      
      // Clear cache and refresh
      variantsCache = null;
      await fetchVariants(true);
      return true;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const updateVariant = async (id: string, values: VariantFormValues) => {
    try {
      const { error } = await supabase
        .from("variants")
        .update({
          name: values.name,
          products: values.products, // Store product IDs
          factories: values.factories,
          description: values.description || null,
          image_url: values.image_url || null,
          add_custom_option: values.add_custom_option,
          variants_order: values.variants_order,
          variant_monogram: values.variant_monogram || {},
          variant_group: values.variant_group || {},
          updated_at: new Date().toISOString()
        })
        .eq("id", id);

      if (error) throw error;
      
      toast({
        title: "Variant Updated",
        description: `${values.name} has been updated.`
      });
      
      // Clear cache and refresh
      variantsCache = null;
      await fetchVariants(true);
      return true;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const deleteVariant = async (variant: any) => {
    try {
      const { error } = await supabase
        .from("variants")
        .delete()
        .eq("id", variant.id);

      if (error) throw error;
      
      toast({
        title: "Variant Deleted",
        description: `${variant.name} has been deleted.`
      });
      
      // Clear cache and refresh
      variantsCache = null;
      await fetchVariants(true);
      return true;
    } catch (error: any) {
      toast({
        title: "Error deleting variant",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  useEffect(() => {
    if (products.length > 0) {
      fetchVariants();
    }
  }, [products, fetchVariants]);

  // Initial fetch
  useEffect(() => {
    fetchVariants();
  }, [fetchVariants]);

  return {
    variants,
    loading,
    fetchVariants,
    createVariant,
    updateVariant,
    deleteVariant
  };
}
